import React from 'react';
import { AdminFormFieldRow } from './AdminFormFieldRow';
import { WORKER_REGISTRY_SECTION_EMPLOYMENT, WORKER_REGISTRY_SECTION_PREVIOUS_JOB } from './workerRegistryAdminLayout';
import './ProfileSectionPreview.css';

function formatBloodType(raw: string): string {
  const v = raw.trim();
  const map: Record<string, string> = { A: 'A型', B: 'B型', O: 'O型', AB: 'AB型' };
  return map[v] ?? v;
}

function formatYesNo(raw: string): string {
  const v = raw.trim().toLowerCase();
  if (v === 'true' || v === 'yes' || v === 'はい') return 'はい';
  if (v === 'false' || v === 'no' || v === 'いいえ') return 'いいえ';
  return raw.trim() || '—';
}

function formatDateJa(raw: string): string {
  const v = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return v || '—';
  const d = new Date(`${v}T12:00:00`);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatEmploymentPeriod(start: string, end: string): string {
  const s = start.trim();
  const e = end.trim();
  if (!s && !e) return '—';
  if (s && e) return `${formatDateJa(s)} ～ ${formatDateJa(e)}`;
  if (s) return `${formatDateJa(s)} ～`;
  return `～ ${formatDateJa(e)}`;
}

function fieldChanged(
  values: Record<string, string>,
  snapshotValues: Record<string, string> | undefined,
  key: string
): boolean {
  if (!snapshotValues || !(key in snapshotValues)) return false;
  return (values[key] ?? '') !== (snapshotValues[key] ?? '');
}

export function WorkerRegistryAdminFieldEditor({
  values,
  editing,
  onChange,
  snapshotValues,
}: {
  values: Record<string, string>;
  editing: boolean;
  onChange: (key: string, value: string) => void;
  snapshotValues?: Record<string, string>;
}) {
  const blood = values.bloodType ?? '';
  const occupation = values.occupation ?? '';
  const hireDate = values.hireDate ?? '';
  const retirementDate = values.retirementDate ?? '';
  const reasonForDismissal = values.reasonForDismissal ?? '';
  const careerHistoryListedInResume = values.careerHistoryListedInResume ?? '';
  const myNumber = values.myNumberLinked ?? '';
  const constructionYears = values.constructionExperienceYears ?? '';
  const education = values.highestEducation ?? '';
  const workStart = values.workStartedDate ?? '';
  const workEnd = values.workEndedDate ?? '';
  const company = values.previousCompanyName ?? '';
  const prefecture = values.previousCompanyPrefecture ?? '';
  const showPreviousJobFields = constructionYears.trim().length > 0;

  const periodDisplay = formatEmploymentPeriod(workStart, workEnd);
  const periodSnap =
    snapshotValues != null
      ? formatEmploymentPeriod(snapshotValues.workStartedDate ?? '', snapshotValues.workEndedDate ?? '')
      : undefined;
  const periodChanged = periodSnap != null && periodSnap !== periodDisplay;

  return (
    <div className="profile-section-preview__content">
      <AdminFormFieldRow
        label="血液型"
        value={formatBloodType(blood)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'bloodType')}
        compareValue={
          snapshotValues?.bloodType != null ? formatBloodType(snapshotValues.bloodType) : undefined
        }
      >
        <select
          className="profile-section-preview__input"
          value={blood}
          onChange={(e) => onChange('bloodType', e.target.value)}
        >
          <option value="">—</option>
          <option value="A">A型</option>
          <option value="B">B型</option>
          <option value="O">O型</option>
          <option value="AB">AB型</option>
        </select>
      </AdminFormFieldRow>

      <h3 className="profile-section-preview__section-title">{WORKER_REGISTRY_SECTION_EMPLOYMENT}</h3>

      <AdminFormFieldRow
        label="職種（招待の管理者入力）"
        value={occupation || '—'}
        editing={false}
        changed={fieldChanged(values, snapshotValues, 'occupation')}
        compareValue={snapshotValues?.occupation}
      />

      <AdminFormFieldRow
        label="雇用年月日（招待の管理者入力）"
        value={formatDateJa(hireDate)}
        editing={false}
        changed={fieldChanged(values, snapshotValues, 'hireDate')}
        compareValue={
          snapshotValues?.hireDate != null ? formatDateJa(snapshotValues.hireDate) : undefined
        }
      />

      <AdminFormFieldRow
        label="退職年月日"
        value={formatDateJa(retirementDate)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'retirementDate')}
        compareValue={
          snapshotValues?.retirementDate != null
            ? formatDateJa(snapshotValues.retirementDate)
            : undefined
        }
      >
        <input
          type="date"
          className="profile-section-preview__input"
          value={retirementDate}
          onChange={(e) => onChange('retirementDate', e.target.value)}
        />
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="解雇・退職の事由"
        value={reasonForDismissal}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'reasonForDismissal')}
        compareValue={snapshotValues?.reasonForDismissal}
      >
        <input
          className="profile-section-preview__input"
          value={reasonForDismissal}
          onChange={(e) => onChange('reasonForDismissal', e.target.value)}
        />
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="職歴は履歴書に記載"
        value={formatYesNo(careerHistoryListedInResume)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'careerHistoryListedInResume')}
        compareValue={
          snapshotValues?.careerHistoryListedInResume != null
            ? formatYesNo(snapshotValues.careerHistoryListedInResume)
            : undefined
        }
      >
        <select
          className="profile-section-preview__input"
          value={careerHistoryListedInResume}
          onChange={(e) => onChange('careerHistoryListedInResume', e.target.value)}
        >
          <option value="">—</option>
          <option value="true">はい</option>
          <option value="false">いいえ</option>
        </select>
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="マイナ保険証の紐づけ"
        value={formatYesNo(myNumber)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'myNumberLinked')}
        compareValue={
          snapshotValues?.myNumberLinked != null
            ? formatYesNo(snapshotValues.myNumberLinked)
            : undefined
        }
      >
        <select
          className="profile-section-preview__input"
          value={myNumber}
          onChange={(e) => onChange('myNumberLinked', e.target.value)}
        >
          <option value="">—</option>
          <option value="true">はい</option>
          <option value="false">いいえ</option>
        </select>
      </AdminFormFieldRow>

      <h3 className="profile-section-preview__section-title">{WORKER_REGISTRY_SECTION_PREVIOUS_JOB}</h3>

      <AdminFormFieldRow
        label="土木工事経験年数"
        value={constructionYears}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'constructionExperienceYears')}
        compareValue={snapshotValues?.constructionExperienceYears}
      >
        <input
          type="number"
          min={0}
          max={80}
          className="profile-section-preview__input"
          value={constructionYears}
          onChange={(e) => onChange('constructionExperienceYears', e.target.value)}
        />
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="最終学歴"
        value={education}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'highestEducation')}
        compareValue={snapshotValues?.highestEducation}
      >
        <input
          className="profile-section-preview__input"
          value={education}
          onChange={(e) => onChange('highestEducation', e.target.value)}
        />
      </AdminFormFieldRow>

      {showPreviousJobFields && (
        <>
          <AdminFormFieldRow
            label="就業期間"
            value={periodDisplay}
            editing={editing}
            changed={periodChanged}
            compareValue={periodSnap}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="date"
                className="profile-section-preview__input"
                value={workStart}
                onChange={(e) => onChange('workStartedDate', e.target.value)}
                aria-label="就業期間（開始）"
              />
              <span aria-hidden>～</span>
              <input
                type="date"
                className="profile-section-preview__input"
                value={workEnd}
                onChange={(e) => onChange('workEndedDate', e.target.value)}
                aria-label="就業期間（終了）"
              />
            </div>
          </AdminFormFieldRow>

          <AdminFormFieldRow
            label="会社名"
            value={company}
            editing={editing}
            changed={fieldChanged(values, snapshotValues, 'previousCompanyName')}
            compareValue={snapshotValues?.previousCompanyName}
          >
            <input
              className="profile-section-preview__input"
              value={company}
              onChange={(e) => onChange('previousCompanyName', e.target.value)}
            />
          </AdminFormFieldRow>

          <AdminFormFieldRow
            label="所在地"
            value={prefecture}
            editing={editing}
            changed={fieldChanged(values, snapshotValues, 'previousCompanyPrefecture')}
            compareValue={snapshotValues?.previousCompanyPrefecture}
          >
            <input
              className="profile-section-preview__input"
              value={prefecture}
              onChange={(e) => onChange('previousCompanyPrefecture', e.target.value)}
            />
          </AdminFormFieldRow>
        </>
      )}
    </div>
  );
}

export default WorkerRegistryAdminFieldEditor;
